import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Crown,
  Film,
  Handshake,
  HelpCircle,
  Home,
  BarChart3,
  TrendingUp,
  LogOut,
  MoreHorizontal,
  Share2,
  ShieldAlert,
  Sparkles,
  Users,
  X,
  Network,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { subscribeRazorpayMessages } from '@/lib/billing/razorpayCheckout';
import { leftTopArrowGradient } from '@/assets/svgs/gradientIcons';

const SIDEBAR_COLLAPSED_KEY = 'twinnee_creator_sidebar_collapsed';

/** Scene / Video editor / Workflow — full-bleed, no Creator Studio sidebar. */
function isEditorWorkspacePath(pathname = '') {
  return (
    /^\/narration\/[^/]+\/(edit|video-editor|workflow)\/?$/.test(pathname) ||
    /^\/stories\/[^/]+\/(edit|video-editor|workflow)\/?$/.test(pathname)
  );
}

function resolveActiveNav(pathname) {
  if (pathname.startsWith('/channel/trends')) return 'trends';
  if (pathname.startsWith('/channel')) return 'channel';
  if (pathname.startsWith('/tasks')) return 'projects';
  if (pathname.startsWith('/partners')) return 'partners';
  if (pathname.startsWith('/network')) return 'network';
  if (pathname.startsWith('/social-publish')) return 'publish';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/pricing') || pathname.startsWith('/billing')) return 'upgrade';
  if (pathname.startsWith('/tutorial') || pathname.startsWith('/learning') || pathname === '/script-audio-align') return 'help';
  if (pathname.startsWith('/dashboard')) return 'home';
  return 'home';
}

/**
 * Shared Creator Studio shell: left sidebar stays visible across
 * Home, Task history, Partners, Publish, Admin, Pricing, Help.
 */
export function CreatorStudioLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeNav = resolveActiveNav(location.pathname);
  const hideSidebar = isEditorWorkspacePath(location.pathname);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [taskCount, setTaskCount] = useState(0);
  // 'loading' | 'none' | 'unpublished' | 'published'
  const [networkStatus, setNetworkStatus] = useState('loading');
  const [staffRole, setStaffRole] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('storycraft_user') || '{}')?.role;
    } catch {
      return undefined;
    }
  });

  const showAdmin = staffRole === 'admin' || staffRole === 'moderator';
  const isAuthed = Boolean(localStorage.getItem('storycraft_token'));
  const [currentPlanName, setCurrentPlanName] = useState('');
  const planNavLabel = currentPlanName ? `Plan: ${currentPlanName}` : 'Upgrade Plan';

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAuthed) return;
      try {
        const [statsRes, meRes, networkRes] = await Promise.allSettled([
          api.get('/dashboard/stats'),
          api.get('/auth/me'),
          api.get('/network/me'),
        ]);
        if (cancelled) return;
        if (statsRes.status === 'fulfilled') {
          const data = statsRes.value?.data || {};
          const counted = Number(data.tasks);
          setTaskCount(
            Number.isFinite(counted)
              ? counted
              : (Number(data.stories) || 0) + (Number(data.narration_projects) || 0),
          );
        }
        if (meRes.status === 'fulfilled' && meRes.value?.data?.role) {
          setStaffRole(meRes.value.data.role);
          try {
            const prev = JSON.parse(localStorage.getItem('storycraft_user') || '{}');
            localStorage.setItem(
              'storycraft_user',
              JSON.stringify({ ...prev, ...meRes.value.data })
            );
          } catch {
            /* ignore */
          }
        }
        if (networkRes.status === 'fulfilled') {
          const prof = networkRes.value?.data?.profile;
          if (!prof) setNetworkStatus('none');
          else if (prof.published) setNetworkStatus('published');
          else setNetworkStatus('unpublished');
        } else {
          setNetworkStatus('none');
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) {
      setCurrentPlanName('');
      return undefined;
    }
    let cancelled = false;
    const loadPlan = () => {
      api.get('/billing/wallet')
        .then(({ data }) => {
          if (cancelled) return;
          const name = data?.current_plan_name || (data?.paid ? 'Paid' : '');
          setCurrentPlanName(name);
        })
        .catch(() => {});
    };
    loadPlan();
    window.addEventListener('focus', loadPlan);
    const unsubscribe = subscribeRazorpayMessages({ onSuccess: loadPlan });
    return () => {
      cancelled = true;
      window.removeEventListener('focus', loadPlan);
      unsubscribe();
    };
  }, [isAuthed]);

  const handleLogout = () => {
    localStorage.removeItem('storycraft_token');
    localStorage.removeItem('storycraft_user');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const goHome = () => navigate('/dashboard');

  const navActive = (id) => activeNav === id;

  const goMobile = (path) => {
    setMobileMoreOpen(false);
    navigate(path);
  };

  return (
    <div
      className="min-h-screen bg-[#0b0e13] text-white font-jakarta flex overflow-x-hidden selection:bg-[#2DD4BF]/30"
      style={{
        // Force Creator Studio tokens so Scene/Editor pages match Dashboard
        // even when the global theme is light ("magical").
        ['--bg-primary']: '#0b0e13',
        ['--bg-secondary']: '#12151d',
        ['--bg-tertiary']: '#161a22',
        ['--text-primary']: '#F8FAFC',
        ['--text-secondary']: '#9CA3AF',
        ['--text-tertiary']: '#6B7280',
        ['--glass-border']: 'rgba(31, 38, 53, 0.95)',
        ['--glass-bg']: 'rgba(18, 21, 29, 0.88)',
        ['--text-heading']: '#F8FAFC',
        ['--text-body']: '#E2E8F0',
        ['--text-muted']: '#9CA3AF',
      }}
    >
      <div
        className="fixed -top-16 -left-16 w-[850px] h-auto pointer-events-none z-0 opacity-60"
        dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }}
      />

      {!hideSidebar ? (
      <aside
        className={`shrink-0 hidden md:flex flex-col justify-between items-center bg-transparent fixed top-0 left-0 bottom-0 z-30 font-jakarta border-none overflow-y-auto scrollbar-none transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          sidebarCollapsed ? 'w-[116px] px-2.5 pt-6 pb-4' : 'w-[230px] px-5 pt-9 pb-6'
        }`}
        data-testid="creator-studio-sidebar"
      >
        <div className={`w-full flex flex-col items-center ${sidebarCollapsed ? 'space-y-5' : 'space-y-6'}`}>
          {sidebarCollapsed ? (
            <div
              onClick={() => setSidebarCollapsed(false)}
              className="w-full rounded-[22px] bg-[#12161f]/90 p-3 border border-white/[0.07] text-center shadow-lg relative overflow-hidden flex flex-col items-center justify-center group cursor-pointer hover:border-white/20 transition-all mb-3"
              title="Click to expand sidebar"
            >
              <div
                className="absolute -top-10 -left-10 w-28 h-28 pointer-events-none opacity-30"
                dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }}
              />
              <div className="w-full text-center">
                <h2 className="text-[12.5px] font-extrabold tracking-wider font-sora bg-gradient-to-r from-[#D0BCFF] via-[#A78BFA] to-[#62FAE3] bg-clip-text text-transparent uppercase leading-tight text-center">
                  Creator
                  <br />
                  Studio
                </h2>
              </div>
              <p className="text-[10px] text-gray-300 font-normal font-sora mt-1 leading-tight text-center">
                AI Production
                <br />
                Engine
              </p>
            </div>
          ) : (
            <div
              onClick={() => setSidebarCollapsed(true)}
              className="flex items-center justify-between group cursor-pointer px-1.5 py-1 rounded-xl hover:bg-white/[0.04] transition-all w-full mb-3"
              title="Click to collapse sidebar"
            >
              <div className="min-w-0">
                <h2 className="text-[16px] font-semibold tracking-tight font-sora bg-gradient-to-r from-[#D0BCFF] via-[#A78BFA] to-[#62FAE3] bg-clip-text text-transparent inline-block">
                  Creator Studio
                </h2>
                <p className="text-[11px] text-gray-400 font-normal font-sora mt-0.5 whitespace-nowrap">
                  AI Production Engine
                </p>
              </div>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[#A78BFA]/80 group-hover:text-[#62FAE3] group-hover:bg-white/[0.05] transition-all shrink-0">
                <ChevronRight className="w-4 h-4 rotate-180 transition-transform duration-300" />
              </div>
            </div>
          )}

          {sidebarCollapsed ? (
            <nav className="flex flex-col items-center gap-3.5 w-full pt-1">
              <button
                type="button"
                onClick={goHome}
                className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
              >
                <Home
                  className={`w-5 h-5 transition-colors ${
                    navActive('home') ? 'text-[#62FAE3]' : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                />
                <span
                  className={`text-[11px] font-medium transition-colors ${
                    navActive('home')
                      ? 'text-[#62FAE3] font-semibold'
                      : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                >
                  Home
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/channel')}
                className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
              >
                <BarChart3
                  className={`w-5 h-5 transition-colors ${
                    navActive('channel') ? 'text-[#62FAE3]' : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                />
                <span
                  className={`text-[10px] leading-tight text-center font-medium transition-colors ${
                    navActive('channel')
                      ? 'text-[#62FAE3] font-semibold'
                      : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                >
                  YouTube
                  <br />
                  Analytics
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/channel/trends')}
                className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
              >
                <TrendingUp
                  className={`w-5 h-5 transition-colors ${
                    navActive('trends') ? 'text-[#62FAE3]' : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                />
                <span
                  className={`text-[10px] leading-tight text-center font-medium transition-colors ${
                    navActive('trends')
                      ? 'text-[#62FAE3] font-semibold'
                      : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                >
                  General
                  <br />
                  Trends
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/tasks')}
                className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer relative"
              >
                <Film
                  className={`w-5 h-5 transition-colors ${
                    navActive('projects') ? 'text-[#62FAE3]' : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                />
                <span
                  className={`text-[11px] font-medium transition-colors ${
                    navActive('projects')
                      ? 'text-[#62FAE3] font-semibold'
                      : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                >
                  Task history
                </span>
                {taskCount > 0 && (
                  <span className="w-2 h-2 rounded-full bg-[#34d399] absolute -top-0.5 right-1.5 ring-2 ring-[#07090d]" />
                )}
              </button>

              {/* Network icon in collapsed sidebar */}
              <button
                type="button"
                onClick={() => navigate(networkStatus === 'published' ? '/network' : '/network/me')}
                className={`flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer relative ${
                  networkStatus === 'none' ? 'animate-pulse' : ''
                }`}
              >
                <div className={`relative ${networkStatus === 'none' ? 'p-1 rounded-full ring-2 ring-[#62FAE3]/60 shadow-[0_0_10px_rgba(98,250,227,0.4)]' : ''}`}>
                  <Network
                    className={`w-5 h-5 transition-colors ${
                      navActive('network')
                        ? 'text-[#62FAE3]'
                        : networkStatus === 'none'
                        ? 'text-[#62FAE3]'
                        : 'text-[#8a94a6] group-hover:text-white'
                    }`}
                  />
                  {networkStatus === 'unpublished' && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-[#07090d]" />
                  )}
                </div>
                <span
                  className={`text-[10px] leading-tight text-center font-medium transition-colors ${
                    navActive('network') || networkStatus === 'none'
                      ? 'text-[#62FAE3] font-semibold'
                      : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                >
                  {networkStatus === 'none' ? 'Join' : networkStatus === 'unpublished' ? 'Profile' : 'Network'}
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/partners/creative')}
                className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
              >
                <Handshake
                  className={`w-5 h-5 transition-colors ${
                    navActive('partners') ? 'text-[#62FAE3]' : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                />
                <span
                  className={`text-[11px] font-medium transition-colors ${
                    navActive('partners')
                      ? 'text-[#62FAE3] font-semibold'
                      : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                >
                  Partners
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/social-publish')}
                className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
              >
                <Share2
                  className={`w-5 h-5 transition-colors ${
                    navActive('publish') ? 'text-[#62FAE3]' : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                />
                <span
                  className={`text-[11px] font-medium transition-colors ${
                    navActive('publish')
                      ? 'text-[#62FAE3] font-semibold'
                      : 'text-[#8a94a6] group-hover:text-white'
                  }`}
                >
                  Publish
                </span>
              </button>

              {showAdmin && (
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
                >
                  <ShieldAlert
                    className={`w-5 h-5 transition-colors ${
                      navActive('admin')
                        ? 'text-amber-300'
                        : 'text-amber-400/90 group-hover:text-amber-300'
                    }`}
                  />
                  <span
                    className={`text-[11px] font-medium transition-colors ${
                      navActive('admin')
                        ? 'text-amber-300 font-semibold'
                        : 'text-amber-400/90 group-hover:text-amber-300'
                    }`}
                  >
                    Admin
                  </span>
                </button>
              )}
            </nav>
          ) : (
            <nav className="space-y-1.5 text-xs font-medium w-full pt-3">
              <button
                type="button"
                onClick={goHome}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                  navActive('home')
                    ? 'bg-[#13322d] text-[#34d399] font-semibold'
                    : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                <Home className="w-4 h-4" />
                <span>Home</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/channel')}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                  navActive('channel')
                    ? 'bg-[#13322d] text-[#34d399] font-semibold'
                    : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                <BarChart3 className="w-4 h-4 shrink-0" />
                <span>YouTube Analytics</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/channel/trends')}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                  navActive('trends')
                    ? 'bg-[#13322d] text-[#34d399] font-semibold'
                    : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                <TrendingUp className="w-4 h-4 shrink-0" />
                <span>General Trends</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/tasks')}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                  navActive('projects')
                    ? 'bg-[#13322d] text-[#34d399] font-semibold'
                    : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                <Film className="w-4 h-4" />
                <span>Task history</span>
                {taskCount > 0 && (
                  <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/10 text-gray-300">
                    {taskCount}
                  </span>
                )}
              </button>

              {/* Creator Network — highlighted CTA when not joined */}
              {networkStatus === 'none' ? (
                <button
                  type="button"
                  onClick={() => navigate('/network/me')}
                  className="w-full relative overflow-hidden flex items-center gap-3 px-4 py-2.5 rounded-full cursor-pointer text-[13px] font-semibold
                    bg-gradient-to-r from-[#D0BCFF]/20 to-[#62FAE3]/20
                    border border-[#62FAE3]/40
                    text-[#62FAE3]
                    shadow-[0_0_16px_rgba(98,250,227,0.15)]
                    animate-pulse hover:animate-none
                    hover:from-[#D0BCFF]/30 hover:to-[#62FAE3]/30
                    hover:shadow-[0_0_24px_rgba(98,250,227,0.3)]
                    transition-all"
                >
                  <Network className="w-4 h-4 flex-shrink-0" />
                  <span>Join Creator Network</span>
                  <span className="absolute right-3 text-[10px] px-1.5 py-0.5 rounded-full bg-[#62FAE3]/20 text-[#62FAE3] font-bold">NEW</span>
                </button>
              ) : networkStatus === 'unpublished' ? (
                <button
                  type="button"
                  onClick={() => navigate('/network/me')}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px]
                    ${navActive('network') ? 'bg-[#13322d] text-[#34d399] font-semibold' : 'text-[#D0BCFF] hover:text-white hover:bg-white/[0.03]'}`}
                >
                  <Network className="w-4 h-4" />
                  <span>Complete Profile</span>
                  <span className="ml-auto w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/network')}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                    navActive('network')
                      ? 'bg-[#13322d] text-[#34d399] font-semibold'
                      : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                  }`}
                >
                  <Network className="w-4 h-4" />
                  <span>Creator Network</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => navigate('/partners/creative')}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                  navActive('partners')
                    ? 'bg-[#13322d] text-[#34d399] font-semibold'
                    : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                <Handshake className="w-4 h-4" />
                <span>CPP Partners</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/social-publish')}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                  navActive('publish')
                    ? 'bg-[#13322d] text-[#34d399] font-semibold'
                    : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                <Share2 className="w-4 h-4" />
                <span>Publish</span>
              </button>

              {showAdmin && (
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                    navActive('admin')
                      ? 'bg-amber-400/10 text-amber-300 font-semibold'
                      : 'text-amber-400/90 hover:text-amber-300 hover:bg-amber-400/[0.06]'
                  }`}
                >
                  <ShieldAlert className="w-4 h-4" />
                  <span>Admin Console</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => navigate('/pricing')}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                  navActive('upgrade')
                    ? 'bg-[#13322d] text-[#34d399] font-semibold'
                    : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                <Crown className="w-4 h-4 text-amber-400" />
                <span>{planNavLabel}</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/script-audio-align', { state: { prefill: { mode: 'topic' } } })}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full transition-all cursor-pointer text-[13px] ${
                  navActive('help')
                    ? 'bg-[#13322d] text-[#34d399] font-semibold'
                    : 'text-[#8a94a6] hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                <HelpCircle className="w-4 h-4" />
                <span>Help</span>
              </button>

              {isAuthed ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-full text-[#8a94a6] hover:text-red-400 hover:bg-red-500/[0.06] transition-all cursor-pointer text-[13px]"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-full text-[#8a94a6] hover:text-white hover:bg-white/[0.03] transition-all cursor-pointer text-[13px]"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Login</span>
                </button>
              )}
            </nav>
          )}
        </div>

        {sidebarCollapsed ? (
          <div className="w-full flex flex-col items-center gap-5 mt-6">
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              className="w-full py-3.5 px-1.5 rounded-[20px] border border-[#2DD4BF]/40 bg-[#0a1819]/60 hover:bg-[#0a1819]/90 hover:border-[#62FAE3] transition-all cursor-pointer text-center group shadow-[0_0_12px_rgba(45,212,191,0.08)]"
            >
              <span className="text-[12px] font-bold font-sora bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] bg-clip-text text-transparent block leading-tight">
                {currentPlanName ? (
                  <>
                    {currentPlanName}
                    <br />
                    Paid
                  </>
                ) : (
                  <>
                    Upgrade
                    <br />
                    Plan
                  </>
                )}
              </span>
            </button>

            <nav className="flex flex-col items-center gap-4 w-full">
              <button
                type="button"
                onClick={() => navigate('/script-audio-align', { state: { prefill: { mode: 'topic' } } })}
                className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
              >
                <HelpCircle className="w-5 h-5 text-[#8a94a6] group-hover:text-white transition-colors" />
                <span className="text-[11px] font-medium text-[#8a94a6] group-hover:text-white transition-colors">
                  Help
                </span>
              </button>

              {isAuthed ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
                >
                  <LogOut className="w-5 h-5 text-[#8a94a6] group-hover:text-red-400 transition-colors" />
                  <span className="text-[11px] font-medium text-[#8a94a6] group-hover:text-red-400 transition-colors">
                    Logout
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="flex flex-col items-center gap-1 group py-0.5 transition-all cursor-pointer"
                >
                  <LogOut className="w-5 h-5 text-[#8a94a6] group-hover:text-white transition-colors" />
                  <span className="text-[11px] font-medium text-[#8a94a6] group-hover:text-white transition-colors">
                    Login
                  </span>
                </button>
              )}
            </nav>
          </div>
        ) : null}
      </aside>
      ) : null}

      <div
        className={`flex-1 min-w-0 min-h-screen flex flex-col relative z-10 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          hideSidebar
            ? 'md:ml-0'
            : sidebarCollapsed
              ? 'md:ml-[116px]'
              : 'md:ml-[230px]'
        } ${hideSidebar ? '' : 'pb-[calc(3.75rem+env(safe-area-inset-bottom))] md:pb-0'}`}
      >
        <Outlet context={{ sidebarCollapsed, hideSidebar }} />
      </div>

      {!hideSidebar ? (
        <>
          {mobileMoreOpen ? (
            <div className="md:hidden fixed inset-0 z-40" data-testid="creator-studio-mobile-more">
              <button
                type="button"
                className="absolute inset-0 bg-black/60"
                aria-label="Close menu"
                onClick={() => setMobileMoreOpen(false)}
              />
              <div className="absolute inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] mx-3 mb-2 rounded-2xl border border-white/10 bg-[#12161f] p-3 shadow-2xl max-h-[70vh] overflow-y-auto">
                <div className="flex items-center justify-between px-1 pb-2">
                  <p className="text-xs font-semibold text-white">Studio</p>
                  <button
                    type="button"
                    onClick={() => setMobileMoreOpen(false)}
                    className="w-8 h-8 rounded-full text-[#9CA3AF] hover:text-white flex items-center justify-center"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <nav className="flex flex-col gap-1">
                  {[
                    { id: 'channel', label: 'YouTube Analytics', path: '/channel', Icon: BarChart3 },
                    { id: 'trends', label: 'General Trends', path: '/channel/trends', Icon: TrendingUp },
                    { id: 'partners', label: 'CPP Partners', path: '/partners/creative', Icon: Handshake },
                    { id: 'characters', label: 'Characters', path: '/characters', Icon: Users },
                    ...(showAdmin
                      ? [{ id: 'admin', label: 'Admin Console', path: '/admin', Icon: ShieldAlert }]
                      : []),
                    { id: 'upgrade', label: planNavLabel, path: '/pricing', Icon: Crown },
                    {
                      id: 'help',
                      label: 'Help',
                      path: '/script-audio-align',
                      Icon: HelpCircle,
                      state: { prefill: { mode: 'topic' } },
                    },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setMobileMoreOpen(false);
                        navigate(item.path, item.state ? { state: item.state } : undefined);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] ${
                        navActive(item.id)
                          ? 'bg-[#13322d] text-[#34d399] font-semibold'
                          : 'text-[#c5cbd6] hover:bg-white/[0.04]'
                      }`}
                    >
                      <item.Icon className="w-4 h-4 shrink-0" />
                      {item.label}
                    </button>
                  ))}
                  {isAuthed ? (
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-[#8a94a6] hover:text-red-400"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => goMobile('/login')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-[#8a94a6]"
                    >
                      <LogOut className="w-4 h-4" />
                      Login
                    </button>
                  )}
                </nav>
              </div>
            </div>
          ) : null}

          <nav
            className="md:hidden fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#0b0e13]/95 backdrop-blur-xl"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            data-testid="creator-studio-mobile-nav"
          >
            <div className="grid grid-cols-5 h-[3.75rem] px-1">
              {[
                { id: 'home', label: 'Home', path: '/dashboard', Icon: Home },
                { id: 'projects', label: 'Tasks', path: '/tasks', Icon: Film },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goMobile(item.path)}
                  className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${
                    navActive(item.id) ? 'text-[#62FAE3]' : 'text-[#8a94a6]'
                  }`}
                >
                  <item.Icon className="w-5 h-5" />
                  {item.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goMobile('/dashboard?agent=new')}
                className="flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold text-[#62FAE3]"
                data-testid="creator-studio-mobile-create"
              >
                <span className="w-9 h-9 -mt-3 rounded-full bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] text-[#080d14] flex items-center justify-center shadow-lg">
                  <Sparkles className="w-4 h-4" />
                </span>
                Create
              </button>
              <button
                type="button"
                onClick={() => goMobile('/social-publish')}
                className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${
                  navActive('publish') ? 'text-[#62FAE3]' : 'text-[#8a94a6]'
                }`}
              >
                <Share2 className="w-5 h-5" />
                Publish
              </button>
              <button
                type="button"
                onClick={() => setMobileMoreOpen((open) => !open)}
                className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${
                  mobileMoreOpen || ['channel', 'trends', 'partners', 'admin', 'upgrade', 'help'].includes(activeNav)
                    ? 'text-[#62FAE3]'
                    : 'text-[#8a94a6]'
                }`}
                data-testid="creator-studio-mobile-more-toggle"
              >
                {mobileMoreOpen ? <X className="w-5 h-5" /> : <MoreHorizontal className="w-5 h-5" />}
                More
              </button>
            </div>
          </nav>
        </>
      ) : null}
    </div>
  );
}

export default CreatorStudioLayout;
